import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import katex from 'katex';

import { logger } from '@zep/core-logger';

import {
  QUIZ_WORKSHEET_MATCH_CARD_CLASS_NAME,
  QUIZ_WORKSHEET_MATCH_CARD_CONTENT_CLASS_NAME,
  QUIZ_WORKSHEET_NOTE_BOX_CLASS_NAME,
  QUIZ_WORKSHEET_NOTE_CONTENT_CLASS_NAME,
  QUIZ_WORKSHEET_NOTE_ICON_CLASS_NAME,
  QUIZ_WORKSHEET_ORDER_GUIDE_BAR_CLASS_NAME,
  QUIZ_WORKSHEET_OX_ICON_CLASS_NAME,
} from '../../QuizWorksheetBlocks';
import {
  QUIZ_WORKSHEET_PAGE_CLASS_NAME,
  QUIZ_WORKSHEET_PAGE_FOOTER_LOGO_CLASS_NAME,
  QUIZ_WORKSHEET_PAGE_FOOTER_QR_CODE_CLASS_NAME,
  QUIZ_WORKSHEET_PAGE_TITLE_AREA_CLASS_NAME,
} from '../../QuizWorksheetPage';
import {
  QUIZ_WORKSHEET_COLUMN_CLASS_NAME,
  QUIZ_WORKSHEET_PAGE_BACKGROUND_LOGO_CLASS_NAME,
} from '../../QuizWorksheetTwoColumnLayout';

import { adjustBlockquoteElements } from './blockquote';
import { adjustHighlightElements } from './highlight';
import { adjustUnderlineElements } from './underline';

const A4_CONFIG = {
  width: 210,
  height: 297,
};

/**
 * html2canvas 가 글자를 실제 위치보다 아래에 그리는 양. 이미지·아이콘을 이만큼 내려 맞춰 온 값이다.
 *
 * 원인: html2canvas 는 1×1 이미지를 글자 옆에 inline 으로 붙여 baseline 을 잰다
 * (`FontMetrics.parseMetrics`). Tailwind preflight 의 `img { display: block }` 이 그 이미지를
 * 다음 줄로 내려 baseline 이 한 줄 가까이 아래로 측정된다. 측정은 onclone 의 복제 문서가 아니라
 * 원본 `document` 에서 일어나 onclone 으로는 고칠 수 없다 — 그래서 글자가 아닌 쪽을 맞춘다.
 */
const PDF_TEXT_BASELINE_OFFSET = '10px';
const PDF_FRACTION_LINE_TRANSLATE_Y = '10.5px';
const PDF_FRACTION_NUMERATOR_TRANSLATE_Y = '-0.5px';
const PDF_FRACTION_DENOMINATOR_TRANSLATE_Y = '0.5px';

/**
 * Temml이 출력한 <math> MathML 요소를 KaTeX HTML로 변환합니다.
 * html2canvas는 MathML을 렌더링하지 못하므로, PDF 생성 시에만 KaTeX를 사용합니다.
 */
function convertMathMLToKatex(clonedDoc: Document) {
  const mathElements = clonedDoc.querySelectorAll('math');
  mathElements.forEach(math => {
    // Temml의 annotation 태그에서 원본 LaTeX를 추출
    const annotation = math.querySelector(
      'annotation[encoding="application/x-tex"]',
    );
    const latex = annotation?.textContent;
    if (!latex) return;

    const isDisplay = math.classList.contains('tml-display');

    try {
      const span = clonedDoc.createElement('span');
      span.innerHTML = katex.renderToString(latex, {
        throwOnError: false,
        displayMode: isDisplay,
      });
      math.replaceWith(span);
    } catch (e) {
      logger.captureError(e, {
        tags: { feature: 'pdf-math-conversion' },
        extra: { latex },
      });
    }
  });
}

function adjustKatexFractionSpacing(clonedDoc: Document) {
  const clonedFracLine = clonedDoc.querySelectorAll('.frac-line');
  clonedFracLine.forEach(fracLine => {
    const lineElement = fracLine as HTMLElement;
    const lineWrapper = lineElement.parentElement;
    const denominator =
      lineWrapper?.previousElementSibling as HTMLElement | null;
    const numerator = lineWrapper?.nextElementSibling as HTMLElement | null;

    lineElement.style.transform = `translateY(${PDF_FRACTION_LINE_TRANSLATE_Y})`;

    if (numerator) {
      numerator.style.transform = `translateY(${PDF_FRACTION_NUMERATOR_TRANSLATE_Y})`;
    }

    if (denominator) {
      denominator.style.transform = `translateY(${PDF_FRACTION_DENOMINATOR_TRANSLATE_Y})`;
    }
  });
}

export const saveAsPDF = async () => {
  // KaTeX CSS를 PDF 생성 시에만 lazy-load (html2canvas가 cloned DOM에서 스타일 참조)
  await import('katex/dist/katex.min.css');

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pages = document.querySelectorAll(`.${QUIZ_WORKSHEET_PAGE_CLASS_NAME}`);

  for (let index = 0; index < pages.length; index++) {
    const page = pages[index] as HTMLElement;

    const images = page.querySelectorAll('img');
    await Promise.all(
      Array.from(images).map(img => {
        if (!img.complete) {
          return new Promise(resolve => {
            img.onload = img.onerror = resolve;
          });
        }
        return Promise.resolve();
      }),
    );

    const canvas = await html2canvas(page, {
      scale: 4,
      logging: false,
      onclone: clonedDoc => {
        // MathML → KaTeX HTML 변환 (html2canvas가 렌더링할 수 있도록)
        convertMathMLToKatex(clonedDoc);

        const columns = clonedDoc.querySelectorAll(
          `.${QUIZ_WORKSHEET_COLUMN_CLASS_NAME}`,
        );

        //NOTE: 페이지 별 문제 column의 top-padding을 0px로 조정
        columns.forEach(column => {
          (column as HTMLElement).style.paddingTop = '0px';
        });
        const clonedImages = clonedDoc.getElementsByTagName('img');
        const clonedFooterLogo = clonedDoc.querySelectorAll(
          `.${QUIZ_WORKSHEET_PAGE_FOOTER_LOGO_CLASS_NAME}`,
        );
        const clonedFooterQRCode = clonedDoc.querySelectorAll(
          `.${QUIZ_WORKSHEET_PAGE_FOOTER_QR_CODE_CLASS_NAME}`,
        );
        const clonedOXIcons = clonedDoc.querySelectorAll(
          `.${QUIZ_WORKSHEET_OX_ICON_CLASS_NAME}`,
        );
        const clonedOrderGuideBars = clonedDoc.querySelectorAll(
          `.${QUIZ_WORKSHEET_ORDER_GUIDE_BAR_CLASS_NAME}`,
        );
        const clonedNoteIcons = clonedDoc.querySelectorAll(
          `.${QUIZ_WORKSHEET_NOTE_ICON_CLASS_NAME}`,
        );
        // html2canvas 는 글자를 실제보다 아래에 그린다(PDF_TEXT_BASELINE_OFFSET 참고).
        // 글자가 아닌 시각 요소를 같은 만큼 내려 글자와 맞춘다.
        Array.from([
          ...clonedImages,
          ...clonedFooterLogo,
          ...clonedFooterQRCode,
          ...clonedOXIcons,
          ...clonedOrderGuideBars,
          ...clonedNoteIcons,
        ]).forEach(img => {
          (
            img as HTMLElement
          ).style.transform = `translateY(${PDF_TEXT_BASELINE_OFFSET})`;
        });

        // 짝맞추기 보기 카드는 테두리가 글자에 바짝 붙어 있어, 글자만 내려가면 아래 테두리에
        // 걸치고 문제 제목도 첫 카드 위로 겹친다. 카드(테두리·연결 점)를 함께 내리고, 카드 안
        // 글자는 다시 올려 html2canvas 가 내린 만큼을 상쇄한다 — 결과적으로 카드와 글자가
        // 이미지처럼 같은 만큼 내려가 제목과의 간격·카드 안 위치가 화면과 같아진다.
        // 힌트·채점 블록의 회색 배경도 같은 방식으로 맞춘다(아이콘은 위에서 따로 내린다).
        clonedDoc
          .querySelectorAll(
            `.${QUIZ_WORKSHEET_MATCH_CARD_CLASS_NAME}, .${QUIZ_WORKSHEET_NOTE_BOX_CLASS_NAME}`,
          )
          .forEach(card => {
            (
              card as HTMLElement
            ).style.transform = `translateY(${PDF_TEXT_BASELINE_OFFSET})`;
          });
        clonedDoc
          .querySelectorAll(
            `.${QUIZ_WORKSHEET_MATCH_CARD_CONTENT_CLASS_NAME}, .${QUIZ_WORKSHEET_NOTE_CONTENT_CLASS_NAME}`,
          )
          .forEach(content => {
            (
              content as HTMLElement
            ).style.transform = `translateY(-${PDF_TEXT_BASELINE_OFFSET})`;
          });
        const clonedBackgroundLogo = clonedDoc.querySelectorAll(
          `.${QUIZ_WORKSHEET_PAGE_BACKGROUND_LOGO_CLASS_NAME}`,
        );
        clonedBackgroundLogo.forEach(logo => {
          (logo as HTMLElement).style.opacity = '0.8';
        });
        const clonedTitleArea = clonedDoc.querySelectorAll(
          `.${QUIZ_WORKSHEET_PAGE_TITLE_AREA_CLASS_NAME}`,
        );
        clonedTitleArea.forEach(titleArea => {
          (titleArea as HTMLElement).style.gap = '8px';
          (titleArea as HTMLElement).style.paddingBottom = '6px';
        });

        //수식 중 분수 라인과 분자/분모 간격 조정
        adjustKatexFractionSpacing(clonedDoc);

        //수식 중 루트 기호(SVG) 위치 조정 — 각 SVG가 자신의 sqrt에 속하는지 확인
        const clonedSqrt = clonedDoc.querySelectorAll('.mord.sqrt');
        clonedSqrt.forEach(sqrt => {
          const allSvgs = sqrt.querySelectorAll('svg');
          for (const svg of allSvgs) {
            // 이 SVG가 현재 sqrt의 직속 SVG인지 확인 (중첩 sqrt의 SVG가 아닌지)
            if (svg.closest('.mord.sqrt') !== sqrt) continue;
            if (!svg.parentElement) continue;
            svg.parentElement.style.position = 'relative';
            svg.parentElement.style.top = '10px';
            break; // 각 sqrt당 하나의 루트 기호 SVG만 보정
          }
        });

        //NOTE: tiptap 스타일링 요소들이 html2canvas 변환 과정에서 위치가 틀어지는 문제 해결
        adjustHighlightElements(clonedDoc);
        adjustUnderlineElements(clonedDoc);
        adjustBlockquoteElements(clonedDoc);
      },
    });
    const imgData = canvas.toDataURL('image/webp', 1.0);

    if (index > 0) pdf.addPage();

    pdf.addImage(imgData, 'WEBP', 0, 0, A4_CONFIG.width, A4_CONFIG.height);
  }

  const savedPDF = pdf.save('quiz-worksheet.pdf');

  return Promise.resolve(savedPDF.output('blob'));
};
